/**
 * 充值/支付 REST API 服务
 *
 * 封装第一方充值相关的 REST API 调用（/api/payment/*）。
 * 所有请求使用 `credentials: 'include'` 以自动携带 Cookie（JWT）。
 *
 * 与支付宝/微信「支付代理」端点（/api/alipay、/api/wechat）完全独立：
 * 代理端点由另一项目通过 HMAC 签名调用；本服务的端点由前端通过 Cookie 认证调用。
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// HTTP 错误类
export class PaymentApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string
  ) {
    super(message);
    this.name = 'PaymentApiError';
  }
}

export class UnauthorizedError extends PaymentApiError {
  constructor(message: string = '未授权，请重新登录') {
    super(message, 401, 'UNAUTHORIZED');
    this.name = 'UnauthorizedError';
  }
}

export class ValidationError extends PaymentApiError {
  constructor(message: string) {
    super(message, 400, 'VALIDATION_ERROR');
    this.name = 'ValidationError';
  }
}

async function handleResponseError(response: Response): Promise<never> {
  let errorMessage = '请求失败';
  try {
    const data = await response.json();
    errorMessage = data.detail || data.message || errorMessage;
  } catch {
    // 无法解析 JSON
  }
  switch (response.status) {
    case 401:
      throw new UnauthorizedError(errorMessage);
    case 400:
      throw new ValidationError(errorMessage);
    default:
      throw new PaymentApiError(errorMessage, response.status);
  }
}

// ==================== 响应类型 ====================

export interface RechargeResponse {
  order_no: string;
  pay_url: string;
  amount_yuan: string;
  channel: string;
}

export interface BalanceResponse {
  balance_yuan: string;
  balance_fen: number;
}

export interface OrderItem {
  order_no: string;
  amount_yuan: string;
  channel: string;
  status: 'pending' | 'paid' | 'failed' | 'closed';
  subject: string;
  trade_no: string | null;
  created_at: string;
  paid_at: string | null;
}

export interface OrderListResponse {
  items: OrderItem[];
  total: number;
}

// ==================== API 服务 ====================

export class PaymentApiService {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * 发起充值
   *
   * @param amountYuan - 充值金额（元）
   * @param channel - 支付渠道，默认支付宝
   * @returns 订单号 + 支付宝跳转 URL
   */
  async createRecharge(
    amountYuan: number | string,
    channel: 'alipay' | 'wechat' = 'alipay'
  ): Promise<RechargeResponse> {
    const response = await fetch(`${this.baseUrl}/api/payment/recharge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ amount_yuan: String(amountYuan), channel }),
    });
    if (!response.ok) {
      await handleResponseError(response);
    }
    return await response.json();
  }

  /**
   * 查询当前钱包余额
   */
  async getBalance(): Promise<BalanceResponse> {
    const response = await fetch(`${this.baseUrl}/api/payment/balance`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    if (!response.ok) {
      await handleResponseError(response);
    }
    return await response.json();
  }

  /**
   * 查询订单列表
   */
  async getOrders(limit: number = 20): Promise<OrderListResponse> {
    const response = await fetch(`${this.baseUrl}/api/payment/orders?limit=${limit}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    if (!response.ok) {
      await handleResponseError(response);
    }
    return await response.json();
  }

  /**
   * 查询单个订单详情（用于支付完成 return_url 后轮询状态）
   */
  async getOrder(orderNo: string): Promise<OrderItem> {
    const response = await fetch(`${this.baseUrl}/api/payment/orders/${encodeURIComponent(orderNo)}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    if (!response.ok) {
      await handleResponseError(response);
    }
    return await response.json();
  }
}

// 导出单例
export const paymentApi = new PaymentApiService();

export default paymentApi;
